import * as React from 'react';
import {useEffect, useRef, useState} from 'react';

import {Tag} from "features/search/types";
import { useDeviceStore, selectIsMobile } from 'features/ui/store/useDeviceStore';
import {useOutsideClick} from 'components/hooks/useOutsideClick';
import {useSearchStore, selectSearchTags} from 'features/search/store/useSearchStore';

import 'components/organisms/ResidenceSearch/Search/TagsWithFoldOut/style.scss';

import {SearchTag} from 'components/atoms/Tags/SearchTag';

interface Props {
    tags?: Tag[];
    onChange?: (isFolded: boolean) => void;
    onTagRemove?: (tag: Tag) => void;
    focus?: () => void;
    children?: React.ReactNode;
    noTagFold?: boolean;
    noTagRemove?: boolean;
    breakWidth?: number;
}

export const TagsWithFoldout: React.FunctionComponent<Props> = ({
    children, breakWidth,
    noTagFold, noTagRemove,
    onChange, focus, tags
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const foldoutRef = useRef<HTMLDivElement>(null);

    let tagReferences: HTMLElement[] = [];
    const [renderFold, setRenderFold] = useState<boolean>(false);
    const [itemBreakPoint, setItemBreakPoint] = useState<number>(0);
    const [itemDiff, setItemDiff] = useState<number>(0);
    const [showFoldedItems, setShowFoldedItems] = useState<boolean>(false);
    const tagsFromStore = useSearchStore(selectSearchTags);
    const removeSearchTag = useSearchStore(state => state.removeSearchTag);

    const consollidatedTags = tags || tagsFromStore;
    const isMobile = useDeviceStore(selectIsMobile);

    useEffect(() => {
        if(consollidatedTags && consollidatedTags.length === 0) return;

        setRenderFold(false);
        setItemDiff(0);

        setTimeout(() => {
            decideFold();
        }, 0);

    }, [consollidatedTags]);

    const decideFold = () => {
        tagReferences = [];

        if(containerRef.current !== null) {
            const wrapperChildren = containerRef.current.childNodes;

            for (let i = 0; i < wrapperChildren.length; i++) {
                if(wrapperChildren[i]) {
                    tagReferences.push(wrapperChildren[i] as HTMLElement);
                }
            }
        }

        let totalWidth = 0;
        let items = 0;
        let folded = false;

        if(tagReferences.length > 0) {
            for(let i = 0; i < tagReferences.length; i++) {
                totalWidth += tagReferences[i].offsetWidth;

                // @ts-expect-error
                if(totalWidth > breakWidth && consollidatedTags) {
                    folded = true;
                    setRenderFold(folded);
                    setItemBreakPoint(items);
                    setItemDiff(consollidatedTags.length - items);

                    break;
                }

                items++;
            }

            if(!folded) {
                setShowFoldedItems(false);
            }

            if (onChange) onChange(folded);
        }
    };

    const showFoldedItemsFunc = () => {
        if(isMobile || noTagFold) return;
        setShowFoldedItems(true);
        if (focus) focus();
    };

    const closeFoldedItems = () => {
        if(consollidatedTags && consollidatedTags.length === 0) {
            setRenderFold(false);
            setShowFoldedItems(false);
            if (onChange) onChange(false);
        } else {
            setShowFoldedItems(false);
        }
    };

    const onWrapperClick = () => {
        if(!renderFold || isMobile) return;

       showFoldedItemsFunc();
    };

    useOutsideClick({
        refs: [foldoutRef],
        callback: closeFoldedItems,
    });

    return (
        <div
            className={`tags-with-foldOut ${renderFold ?
                'tags-with-foldOut--have-fold' :
                ''}`}
            onClick={onWrapperClick}
            ref={foldoutRef}
        >
            <div className="tags" ref={containerRef}>
                {consollidatedTags && consollidatedTags.slice(0, consollidatedTags.length - itemDiff).map((tag, index) => {
                    return (
                        <SearchTag
                            tag={tag}
                            ghost
                            key={`tag-nonfolded-${index}`}
                            onRemove={() => removeSearchTag(tag)}
                            hideRemove={isMobile || noTagRemove}
                        />
                    )
                })}
            </div>

            {(renderFold && !showFoldedItems ) &&
                <SearchTag
                    tag={{
                        id: 'fold-out',
                        name: `+ ${consollidatedTags && consollidatedTags.length - itemBreakPoint}`,
                        type: 'fold',
                    }}
                    onClick={showFoldedItemsFunc}
                    ghost
                    hideRemove={isMobile || noTagRemove}
                />
            }

            {!isMobile && showFoldedItems &&
                <div className="tags-with-foldOut__folded-tags">
                    {consollidatedTags && consollidatedTags.slice(itemBreakPoint).map((tag, index) => (
                        <SearchTag
                            tag={tag}
                            ghost
                            key={`tag-folded-${index}`}
                            onRemove={() => removeSearchTag(tag)}
                            hideRemove={isMobile}
                        />
                    ))}

                    {children && children}
                </div>
            }
        </div>
    )
};